import React from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  useTheme,
  useMediaQuery,
  Button,
} from '@mui/material';
import { useAuth } from '@/contexts/AuthContext';
import { useAccountBookStore } from '@/store/accountBookStore';
// import { useSubscription } from '../../hooks/useSubscription';
import AccountSwitcher from '../AccountSwitcher/AccountSwitcher';
import { DeleteAccountDialog, SubscriptionManageDialog } from './AccountDialogs';

interface HeaderProps {
  onMenuClick: () => void;
  onStoreManagementClick?: () => void;
}

const Header: React.FC<HeaderProps> = ({ onMenuClick, onStoreManagementClick }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const { user, signOut } = useAuth();
  const { currentAccountBook } = useAccountBookStore();
  
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleProfileClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    await signOut();
    handleClose();
  };

  const [subscriptionOpen, setSubscriptionOpen] = React.useState(false);
  const [deleteOpen, setDeleteOpen] = React.useState(false);

  // 구독/설정 관련 UI 제거

  return (
    <AppBar
      position="fixed"
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + 10,
        // 반투명 + blur — 스크롤 시 콘텐츠가 은은히 비치는 글래스 헤더
        background: 'var(--bg-glass)',
        color: 'var(--fg-default)',
        borderBottom: '1px solid var(--border-subtle)',
        boxShadow: 'none',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
      }}
    >
      <Toolbar sx={{
        minHeight: 72,
        background: 'transparent',
      }}>
        {/* 모바일 메뉴 버튼 */}
        {isMobile && (
          <Button
            variant="text"
            size="small"
            aria-label="메뉴 열기"
            onClick={onMenuClick}
            sx={{ mr: 2 }}
          >
            메뉴
          </Button>
        )}

        {/* 로고 및 제목 */}
        <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
          <Typography
            variant="h6"
            component="div"
            sx={{
              fontFamily: 'var(--font-display)',
              fontWeight: 800,
              letterSpacing: '-0.012em',
              color: 'var(--fg-strong)',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            사장님 가계부
          </Typography>
          
          {/* 현재 가계부 정보 */}
          {currentAccountBook && (
            <Box 
              sx={{ 
                ml: 3, 
                display: { xs: 'none', sm: 'flex' },
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  backgroundColor: 'var(--bg-elev-2)',
                  px: 2,
                  py: 0.75,
                  borderRadius: 'var(--r-12)',
                  border: '1px solid var(--border-subtle)',
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: 'var(--fg-tertiary)',
                    fontSize: '0.7rem',
                    lineHeight: 1,
                    mb: 0.25,
                  }}
                >
                  {currentAccountBook.accountNumber}번 매장
                </Typography>
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: 'var(--fg-strong)',
                    fontWeight: 600,
                    lineHeight: 1.2,
                  }}
                >
                  {currentAccountBook.businessName || currentAccountBook.name}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>

        {/* 매장 전환 드롭다운 */}
        <Box sx={{ mr: isMobile ? 1 : 2 }}>
          <AccountSwitcher />
        </Box>

        {/* 매장 관리 버튼 */}
        {onStoreManagementClick && !isMobile && (
          <Button
            variant="outlined"
            onClick={onStoreManagementClick}
            sx={{
              mr: 2,
              borderColor: 'var(--border-default)',
              color: 'var(--fg-default)',
              '&:hover': {
                borderColor: 'var(--primary)',
                backgroundColor: 'var(--primary-bg)',
              },
            }}
          >
            매장 관리
          </Button>
        )}

        {/* 알림 버튼 */}
        <Button
          variant="text"
          size="small"
          aria-label="알림"
          sx={{ mr: 1 }}
        >
          알림
        </Button>

        {/* 사용자 프로필 */}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton
            onClick={handleProfileClick}
            size="small"
            sx={{ ml: 1 }}
            aria-controls={open ? 'account-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={open ? 'true' : undefined}
          >
            {user?.photoURL ? (
              <Avatar
                src={user.photoURL}
                sx={{ 
                  width: 32, 
                  height: 32,
                }}
              />
            ) : (
              <Avatar
                sx={{ 
                  width: 32, 
                  height: 32,
                  backgroundColor: 'primary.main',
                }}
              >
                {user?.displayName?.charAt(0) || user?.email?.charAt(0)}
              </Avatar>
            )}
          </IconButton>
        </Box>

        {/* 사용자 메뉴 */}
        <Menu
          anchorEl={anchorEl}
          id="account-menu"
          open={open}
          onClose={handleClose}
          onClick={handleClose}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          PaperProps={{
            sx: {
              mt: 1,
              minWidth: 200,
              boxShadow: 'var(--shadow-3)',
              borderRadius: 'var(--r-12)',
              background: 'var(--bg-base)',
              border: '1px solid var(--border-subtle)',
            },
          }}
        >
          {/* 사용자 정보: 이메일만 표시 */}
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'var(--fg-strong)', mb: 0.5 }}>
              이메일
            </Typography>
            <Typography variant="body2" sx={{ color: 'var(--fg-secondary)', fontFamily: 'var(--font-mono)' }}>
              {user?.email || '-'}
            </Typography>
          </Box>

          <Divider sx={{ borderColor: 'var(--border-subtle)' }} />

          <MenuItem onClick={() => setSubscriptionOpen(true)}>구독 관리 · 해지</MenuItem>

          {/* 로그아웃 */}
          <MenuItem onClick={handleLogout} sx={{ color: 'var(--c-red-600)' }}>
            로그아웃
          </MenuItem>

          <MenuItem onClick={() => setDeleteOpen(true)} sx={{ color: 'var(--fg-tertiary)', fontSize: 'var(--size-sm)' }}>
            회원 탈퇴
          </MenuItem>
        </Menu>

        <SubscriptionManageDialog open={subscriptionOpen} onClose={() => setSubscriptionOpen(false)} />
        <DeleteAccountDialog
          open={deleteOpen}
          onClose={() => setDeleteOpen(false)}
          onDeleted={async () => {
            setDeleteOpen(false);
            await signOut();
          }}
          onOpenSubscription={() => setSubscriptionOpen(true)}
        />
      </Toolbar>
    </AppBar>
  );
};

export default Header;
